import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import { useAccount, useSwitchChain } from 'wagmi';
import { base, baseSepolia, Chain } from 'wagmi/chains';
import { getChains } from '@wagmi/core';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { wagmiConfig } from '@/onchain/OnchainProviders';
import { usePersistedState } from '@/hooks/usePersistedState';

type ChainSwitchContextType = {
  showChainSwitchModal: () => void;
  chainSwitchModalVisible: boolean;
  forceSwitchChainId: number | undefined;
  currentChainId: number | undefined;
  supportedChains: readonly [Chain, ...Chain[]];
  handleSwitchChain: ({ chainId }: { chainId: number }) => Promise<void>;
  handleModalClose: () => void;
  isTestnetEnabled: boolean;
  testnetSupported: boolean;
  toggleTestnetMode: () => void;
  testnetModeModalVisible: boolean;
  showTestnetModeModal: () => void;
  hideTestnetModeModal: () => void;
};

const ChainSwitchContext = createContext<ChainSwitchContextType | undefined>(undefined);

export function ChainSwitchProvider({ children }: { children: ReactNode }) {
  const [chainSwitchModalVisible, setChainSwitchModalVisible] = useState(false);
  const showChainSwitchModal = useCallback(() => setChainSwitchModalVisible(true), []);
  const hideChainSwitchModal = useCallback(() => setChainSwitchModalVisible(false), []);

  const [testnetModeModalVisible, setTestnetModeModalVisible] = useState(false);
  const showTestnetModeModal = useCallback(() => setTestnetModeModalVisible(true), []);
  const hideTestnetModeModal = useCallback(() => setTestnetModeModalVisible(false), []);

  const toast = useToast();
  const isProd = process.env.NEXT_PUBLIC_ENVIRONMENT === 'production';

  // Add developer mode toggle state, default to true unless in prod
  const [isTestnetEnabled, setIsTestnetEnabled] = usePersistedState<boolean>(
    isProd ? false : true,
    'isTestnetEnabled',
  );

  const [forceSwitchChainId, setForceSwitchChainId] = useState<number | undefined>(undefined);
  const account = useAccount();
  const currentChainId = account.chainId;

  const availableChains = getChains(wagmiConfig);
  const testnetSupported = availableChains.some((chain) => chain.id === baseSepolia.id);
  const supportedChains = useMemo(() => {
    // if there's only testnet supported, return only the testnet chain
    if (availableChains.length === 1 && testnetSupported) {
      return availableChains;
    }
    if (!isTestnetEnabled) {
      // filter out testnet chains
      return availableChains.filter((chain) => chain.id !== baseSepolia.id);
    }
    return availableChains;
  }, [availableChains, isTestnetEnabled, testnetSupported]);

  const baseSupported = supportedChains.some((chain) => chain.id === base.id);
  const isValidChain =
    !currentChainId || supportedChains.some((chain) => chain.id === currentChainId);

  const targetChainIdToSet = baseSupported ? base.id : baseSepolia.id;
  const { switchChainAsync } = useSwitchChain();

  // Toggle testnet mode
  const toggleTestnetMode = useCallback(() => {
    const newValue = !isTestnetEnabled;
    setIsTestnetEnabled(newValue);

    // Show explanation modal when enabling testnet mode
    if (newValue) {
      showTestnetModeModal();
    }
  }, [isTestnetEnabled, setIsTestnetEnabled, showTestnetModeModal]);

  const handleSwitchChain = useCallback(
    async ({ chainId }: { chainId: number }) => {
      try {
        await switchChainAsync({ chainId });

        const chainToSwitchTo = supportedChains.find((chain) => chain.id === chainId);
        if (chainToSwitchTo?.id !== currentChainId) {
          toast.show(`Switched to ${chainToSwitchTo?.name ?? 'new network'}`, {
            variant: 'bgPositive',
          });
        }

        setForceSwitchChainId(undefined);
        hideChainSwitchModal();
      } catch (error) {
        console.error(`Failed to switch network to ${chainId}:`, error);
      }
    },
    [switchChainAsync, supportedChains, toast, currentChainId, hideChainSwitchModal],
  );

  // Simplify modal close handler
  const handleModalClose = useCallback(() => {
    hideChainSwitchModal();
  }, [hideChainSwitchModal]);

  useEffect(() => {
    if (!currentChainId) return;

    if (!isValidChain && !chainSwitchModalVisible) {
      // Force switch for unsupported chains
      setForceSwitchChainId(targetChainIdToSet);
      showChainSwitchModal();
    } else if (isValidChain && chainSwitchModalVisible) {
      // hide modal if the chain is valid
      hideChainSwitchModal();
    }
  }, [
    isValidChain,
    targetChainIdToSet,
    showChainSwitchModal,
    currentChainId,
    chainSwitchModalVisible,
    hideChainSwitchModal,
  ]);

  // Use useMemo to avoid recreating the context value on every render
  const contextValue = useMemo(
    () => ({
      showChainSwitchModal,
      chainSwitchModalVisible,
      forceSwitchChainId,
      currentChainId,
      supportedChains,
      handleSwitchChain,
      handleModalClose,
      isTestnetEnabled,
      toggleTestnetMode,
      testnetModeModalVisible,
      showTestnetModeModal,
      hideTestnetModeModal,
      testnetSupported,
    }),
    [
      showChainSwitchModal,
      chainSwitchModalVisible,
      forceSwitchChainId,
      currentChainId,
      supportedChains,
      handleSwitchChain,
      handleModalClose,
      isTestnetEnabled,
      toggleTestnetMode,
      testnetModeModalVisible,
      showTestnetModeModal,
      hideTestnetModeModal,
      testnetSupported,
    ],
  );

  return <ChainSwitchContext.Provider value={contextValue}>{children}</ChainSwitchContext.Provider>;
}

export function useChainSwitch() {
  const context = useContext(ChainSwitchContext);
  if (context === undefined) {
    throw new Error('useChainSwitch must be used within a ChainSwitchProvider');
  }
  return context;
}
